/**
 * The staff console: find a dog, then open THE CUSTOMER'S OWN PORTAL for that household.
 *
 * IT IS NOT A SEPARATE RENDERING OF THE RECORD, and that is the requirement rather than an
 * implementation detail. Staff answer "something looks wrong on my profile" — which they can only
 * do while looking at the same screen the client is describing. A parallel staff view answers a
 * question nobody asked, and drifts out of step the first time either side changes.
 *
 * So the only staff-specific parts are the two this console owns: choosing a household, and
 * fetching it. Below `PortalProvider` everything is the exact code a customer runs — same shell,
 * same navigation, same four screens, same review wizard.
 *
 * An earlier version of this file rendered its own summary of the record. It was reported as
 * missing "vets information and other", because it was: it showed about 13 fields of the 60+ the
 * record carries. That is the failure mode a duplicate view has by default, and deleting it is the
 * fix.
 *
 * The one thing staff still cannot see is the emergency contact — a third party who never filled
 * in a form. The client's own portal masks it behind a deliberate, individually audited reveal,
 * and it stays masked here for the same reason.
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react'
import type { StaffDogSummary, StaffSessionInfo } from '@ft/shared'

import { AppShell } from '../components/AppShell'
import { PortalProvider } from '../state/portal'
import { adminPinSignIn, staffConsume, staffDogs, staffHousehold, staffSession, staffSignIn, staffSignOut } from './api'
import { AdminZone } from './AdminZone'

/** Strip the token from the address bar the moment it is spent. */
function tokenFromUrl(): string | null {
  const token = new URL(window.location.href).searchParams.get('token')
  return token !== null && token.length >= 10 ? token : null
}
function stripToken(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('token')) return
  url.searchParams.delete('token')
  window.history.replaceState(null, '', `/staff${url.search}${url.hash}`)
}

type Phase =
  | { readonly state: 'checking' }
  | { readonly state: 'signed-out'; readonly notice: string | null }
  | { readonly state: 'in'; readonly who: StaffSessionInfo }

export function StaffApp(): ReactElement {
  const [phase, setPhase] = useState<Phase>({ state: 'checking' })

  useEffect(() => {
    let live = true
    const token = tokenFromUrl()

    void (async () => {
      if (token !== null) {
        stripToken()
        const consumed = await staffConsume(token)
        if (!live) return
        if (consumed.ok) {
          setPhase({ state: 'in', who: consumed.data })
          return
        }
        setPhase({ state: 'signed-out', notice: consumed.message })
        return
      }

      const session = await staffSession()
      if (!live) return
      setPhase(session.ok ? { state: 'in', who: session.data } : { state: 'signed-out', notice: null })
    })()

    return () => {
      live = false
    }
  }, [])

  if (phase.state === 'checking') {
    return <Centred><p className="text-muted">Checking…</p></Centred>
  }
  if (phase.state === 'signed-out') {
    // Super Administrators sign in with a PIN and password at /admin; everyone else gets a link.
    return isAdminZone ? <AdminSignIn notice={phase.notice} /> : <StaffSignIn notice={phase.notice} />
  }

  /*
    The zone is gated on the ROLE, not on the path. Reaching /admin with an ordinary staff session
    gets the console, not a locked door with a hint behind it — and every admin API route re-checks
    `role = 'super'` server-side anyway, so this only decides what is drawn.
  */
  if (isAdminZone && phase.who.role === 'super') {
    return (
      <AdminZone
        who={phase.who}
        onSignOut={() => void staffSignOut().then(() => window.location.replace('/admin'))}
        customers={<Console who={phase.who} chrome={false} />}
      />
    )
  }
  return <Console who={phase.who} chrome />
}

/** True on /admin. Decided once, from the path the page was opened with. */
const isAdminZone = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')

/**
 * Sign in to the Super Administration Zone: email, PIN and password together.
 *
 * NO MAGIC LINK, by owner decision. The three go in one request and the server answers every
 * failure identically, so nothing here reveals which of them was wrong — or whether the address is
 * an administrator at all.
 */
function AdminSignIn({ notice }: { readonly notice: string | null }): ReactElement {
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(notice)

  return (
    <Centred>
      <section className="card elev-sm" style={{ gap: 0, padding: '26px 24px' }}>
        <div className="card-kicker">Super Administration</div>
        <h1 style={{ margin: '0 0 7px', fontSize: '26px' }}>Fairy Tails</h1>
        <p className="text-muted" style={{ margin: '0 0 18px', fontSize: '14px', lineHeight: 1.55 }}>
          Administrator access to everything in the portal. You will stay signed in until you choose
          Logout.
        </p>

        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            if (busy) return
            setBusy(true)
            setFailure(null)
            void adminPinSignIn(email.trim(), pin.trim(), password).then((result) => {
              setBusy(false)
              if (result.ok) {
                // A full navigation, so the zone mounts from a clean state with the new cookie.
                window.location.replace('/admin')
                return
              }
              setFailure(result.message)
            })
          }}
        >
          <div className="field">
            <label htmlFor="ft-admin-email">Email address</label>
            <input
              className="input"
              id="ft-admin-email"
              type="email"
              autoComplete="username"
              autoCapitalize="off"
              value={email}
              disabled={busy}
              onChange={(event) => setEmail(event.target.value)}
              style={{ minHeight: '44px' }}
            />
          </div>

          <div className="field" style={{ marginTop: '12px' }}>
            <label htmlFor="ft-admin-pin">PIN</label>
            <input
              className="input"
              id="ft-admin-pin"
              // `type=password` so it is not shoulder-readable; inputMode numeric so a phone shows
              // a keypad. NOT type=number — that would strip a leading zero from the PIN.
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              disabled={busy}
              onChange={(event) => setPin(event.target.value)}
              style={{ minHeight: '44px' }}
            />
          </div>

          <div className="field" style={{ marginTop: '12px' }}>
            <label htmlFor="ft-admin-password">Password</label>
            <input
              className="input"
              id="ft-admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={busy}
              onChange={(event) => setPassword(event.target.value)}
              style={{ minHeight: '44px' }}
            />
          </div>

          {failure === null ? null : (
            <p role="alert" style={{ margin: '14px 0 0', fontSize: '13px', lineHeight: 1.55, color: 'var(--color-accent-800)' }}>
              {failure}
            </p>
          )}

          <button type="submit" className="btn btn-primary btn-block" disabled={busy} style={{ minHeight: '44px', marginTop: '18px' }}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
      </section>
    </Centred>
  )
}

function Centred({ children }: { readonly children: ReactElement | string }): ReactElement {
  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '26px 18px' }}>
      <div style={{ width: 'min(430px, 100%)' }}>{children}</div>
    </main>
  )
}

function StaffSignIn({ notice }: { readonly notice: string | null }): ReactElement {
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<string | null>(null)
  const [devLink, setDevLink] = useState<string | null>(null)
  /*
    A FAILED REQUEST MUST SAY SO.

    This screen originally did nothing at all when the request failed: the button went back to
    "Email me a link" and no message appeared. Reported from production as "it fails to send" —
    which is precisely what it looks like when the rate limiter (5 per 10 minutes, keyed on IP)
    turns you away and the UI stays silent about it. Never leave the failure branch empty.
  */
  const [failure, setFailure] = useState<string | null>(null)

  return (
    <Centred>
      <section className="card elev-sm" style={{ gap: 0, padding: '26px 24px' }}>
        <div className="card-kicker">Staff</div>
        <h1 style={{ margin: '0 0 7px', fontSize: '26px' }}>Fairy Tails console</h1>
        <p className="text-muted" style={{ margin: '0 0 18px', fontSize: '14px', lineHeight: 1.55 }}>
          For staff only. Enter your work address and we will email you a link. There is no password.
        </p>

        {notice === null ? null : (
          <p role="alert" style={{ margin: '0 0 14px', padding: '12px 16px', borderRadius: '22px', background: 'var(--color-accent-100)', color: 'var(--color-accent-800)', fontSize: '13px' }}>
            {notice}
          </p>
        )}

        {sent === null ? (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              if (sending) return
              setSending(true)
              setFailure(null)
              void staffSignIn(email.trim()).then((result) => {
                setSending(false)
                if (result.ok) {
                  setSent(result.data.message)
                  setDevLink(result.data.devLink ?? null)
                  return
                }
                setFailure(
                  result.status === 429
                    ? 'Too many sign-in attempts from here. Wait ten minutes and try again — any link already emailed to you still works.'
                    : result.message
                )
              })
            }}
          >
            <div className="field">
              <label htmlFor="ft-staff-email">Work email address</label>
              <input
                className="input"
                id="ft-staff-email"
                type="email"
                autoComplete="email"
                autoCapitalize="off"
                value={email}
                disabled={sending}
                onChange={(event) => setEmail(event.target.value)}
                style={{ minHeight: '44px' }}
              />
            </div>
            {failure === null ? null : (
              <p role="alert" style={{ margin: '12px 0 0', fontSize: '13px', lineHeight: 1.55, color: 'var(--color-accent-800)' }}>
                {failure}
              </p>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={sending} style={{ minHeight: '44px', marginTop: '18px' }}>
              {sending ? 'Sending…' : 'Email me a link'}
            </button>
          </form>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.55 }}>{sent}</p>
            {devLink === null ? null : (
              <p style={{ marginTop: '12px', fontSize: '13px' }}>
                <span className="tag tag-accent" style={{ fontWeight: 700 }}>Development only</span>{' '}
                <a href={devLink} style={{ overflowWrap: 'anywhere' }}>Open the link</a>
              </p>
            )}
          </>
        )}
      </section>
    </Centred>
  )
}

function Console({ who, chrome = true }: { readonly who: StaffSessionInfo; readonly chrome?: boolean }): ReactElement {
  const [query, setQuery] = useState('')
  const [dogs, setDogs] = useState<readonly StaffDogSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<StaffDogSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Debounced so typing a dog's name is one request at the end, not one per keystroke.
  useEffect(() => {
    let live = true
    const timer = window.setTimeout(() => {
      setLoading(true)
      void staffDogs(query).then((result) => {
        if (!live) return
        setLoading(false)
        if (result.ok) {
          setDogs(result.data.dogs)
          setTotal(result.data.total)
          setError(null)
        } else {
          setError(result.message)
        }
      })
    }, 220)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [query])

  const exit = useCallback(() => setOpen(null), [])

  /*
    THE RECORD IS THE CUSTOMER'S OWN PORTAL, not a staff rendering of it.

    `PortalProvider` is handed a loader that fetches the chosen household instead of the signed-in
    client's own record, and everything below it — AppShell, the sidebar, the four screens, the
    review wizard — is the exact code a customer runs. That is the point: staff troubleshooting
    "something looks wrong on my profile" have to be looking at the same pixels, and a separate
    staff rendering would answer a question nobody asked. It also cannot fall behind: there is no
    second view to keep in step.

    `key` on the provider is load-bearing. Without it, switching from one household to another
    reuses the same provider instance, whose record fetch runs once on mount — so the second dog
    would render the first dog's record.
  */
  if (open !== null) {
    return (
      <PortalProvider
        key={open.customerId}
        loadRecord={(options) => staffHousehold(open.customerId, options)}
        viewingAs={{ staffEmail: who.email, householdName: open.ownerName, onExit: exit }}
      >
        <AppShell />
      </PortalProvider>
    )
  }

  return (
    <div style={{ minHeight: chrome ? '100vh' : 'auto' }}>
      {/*
        Suppressed when embedded in the Super Administration Zone, which has its own header. Two
        stacked sticky bars, each with a Sign out, would leave an administrator picking between two
        identical-looking ways to leave.
      */}
      {!chrome ? null : (
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '12px 18px',
          background: 'var(--color-accent-800)',
          color: 'var(--color-bg)',
        }}
      >
        <strong style={{ fontSize: '14px' }}>Fairy Tails · staff console</strong>
        <span style={{ fontSize: '12.5px', opacity: 0.85 }}>{who.email}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void staffSignOut().then(() => window.location.replace('/staff'))}
            style={{ minHeight: '38px', fontSize: '13px', color: 'var(--color-bg)' }}
          >
            Sign out
          </button>
        </span>
      </header>
      )}

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '20px 18px 60px' }}>
        {error === null ? null : (
          <p role="alert" className="card" style={{ padding: '14px 18px', marginBottom: '16px', fontSize: '13.5px' }}>
            {error}
          </p>
        )}

        <>
          <div className="field" style={{ marginBottom: '14px' }}>
              <label htmlFor="ft-staff-search">Search by dog, owner or email</label>
              <input
                className="input"
                id="ft-staff-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="e.g. Milo"
                style={{ minHeight: '44px' }}
              />
            </div>
            <p className="text-muted" style={{ margin: '0 0 12px', fontSize: '12.5px' }}>
              {loading ? 'Searching…' : `${dogs.length} shown${total > dogs.length ? ` of ${total} matching` : ''}`}
            </p>
            <div style={{ display: 'grid', gap: '8px' }}>
              {dogs.map((dog) => (
                <button
                  key={dog.dogId}
                  type="button"
                  onClick={() => setOpen(dog)}
                  className="card"
                  style={{ textAlign: 'left', cursor: 'pointer', border: 0, font: 'inherit', padding: '14px 18px', display: 'grid', gap: '2px' }}
                >
                  <strong style={{ fontSize: '15px' }}>{dog.dogName}</strong>
                  <span className="text-muted" style={{ fontSize: '12.5px' }}>
                    {dog.ownerName ?? 'No owner name'} · {dog.email ?? 'no email on file'}
                  </span>
                </button>
              ))}
            {!loading && dogs.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '13.5px' }}>No dog matches that.</p>
            ) : null}
          </div>
        </>
      </main>
    </div>
  )
}
